// Task #519: the Safari permission popup is retired — greeting playback is
// attempt-first, with the page-wide unlock kit (GreetingAudioUnlock.js) as
// the hold-until-first-tap safety net.
import {
    claimGreetingAudioElement,
    releaseGreetingAudioElement,
    armGreetingStart,
    hasUnlockGesture,
    commitGreetingAudioUnlock,
} from "./GreetingAudioUnlock.js";
import { setGreetingAudioGranted } from "./ChatController.js";
import { A2FLipSyncDriver } from "./A2FLipSyncDriver.js";
import { shouldUseA2FLipSync } from "./A2FLipSyncConfig.js";
import { getClientTimeInfo } from "./ClientTimeOfDay.js";
// Shared A2F forward-pacing + frame-starvation tuning — single source of
// truth shared with the chat path (RealtimeLLMToElevenLabs.js). Aliased to
// the GREETING_-prefixed names this file already uses.
import {
    A2F_FORWARD_PACING_ENABLED as GREETING_A2F_FORWARD_PACING_ENABLED,
    A2F_FORWARD_PACING_RATE as GREETING_A2F_FORWARD_PACING_RATE,
    A2F_FORWARD_PACING_WARMUP_MS as GREETING_A2F_FORWARD_PACING_WARMUP_MS,
} from "./A2FTuning.js";

// ==============================
// Safari + Gesture + Playback Helpers (module-scope)
// ==============================
function isSafari() {
    const ua = navigator.userAgent;
    const isCriOS = /\bCriOS\b/.test(ua); // Chrome on iOS
    const isFxiOS = /\bFxiOS\b/.test(ua); // Firefox on iOS
    const isIOS = /iP(ad|hone|od)/.test(ua);
    const isWebKit = /WebKit/.test(ua);
    const isSafariDesktop = /^((?!chrome|android|edg|opr|brave).)*safari/i.test(
        ua,
    );
    return (isIOS && isWebKit && !isCriOS && !isFxiOS) || isSafariDesktop;
}

// ==============================
// Greeting A2F robustness tuning.
//
// The forward-pacer and frame-starvation tuning values are imported from
// the shared `A2FTuning.js` module so the greeting and chat paths share a
// single source of truth and can never silently drift apart.
//
//  - Forward-pacer: ElevenLabs/decoded PCM bursts faster than the NVIDIA
//    A2F server can consume; forwarding it all in one tight loop lets the
//    bridge queue grow seconds deep and starves lip-sync. Pace the
//    bridge-bound copy to ~real-time after a short warm-up. Speaker audio
//    (the <audio> element) is never throttled by this.
//  - Pause-on-underrun gate: when the A2F frame queue runs dry
//    mid-greeting, pause the speaker audio AND freeze the driver clock
//    together, resuming once frames refill (hysteresis low/high water) or
//    the session ends — so the mouth never freezes while audio plays on.
//
// `GREETING_A2F_PCM_*` below are greeting-local chunking details (how the
// decoded PCM buffer is sliced for the pacer) and are not shared.
// ==============================
const GREETING_A2F_PCM_BYTES_PER_MS = 32; // 16 kHz * 16-bit * mono = 32000 B/s
const GREETING_A2F_PCM_CHUNK_BYTES = 3200; // ~100 ms per chunk

// Task #519: the module-scope `__audioGestureUnlocked` flag-only listeners
// that lived here were the seed of the page-wide unlock kit and are replaced
// by it — GreetingAudioUnlock harvests gestures from first paint, resumes
// the TTS AudioContext and blesses the persistent greeting element inside
// the gesture (and still maintains the compat flag).

/**
 * Task #519 — attempt-first greeting playback. The Safari "May <legend>
 * read their first words to you?" permission card is GONE; this helper
 * simply tries to play on EVERY browser:
 *   - play() resolves → voice greeting (on Safari the element is the kit's
 *     persistent blessed one, so an earlier harvested gesture or an allowed
 *     autoplay both land here);
 *   - play() rejects with ZERO interaction so far → HOLD the greeting
 *     (never burn it as text), show the minimal on-theme pill, and start
 *     playback INSIDE the first tap anywhere (in-gesture play() is always
 *     allowed);
 *   - play() rejects after a real gesture already unlocked audio → genuine
 *     playback failure → graceful text fallback, exactly as before.
 * Returns true if audio started; false if we rendered text instead.
 * @param {HTMLAudioElement} audioEl - The audio element to play
 * @param {string} text - The greeting text
 * @param {Object} chatController - The chat controller instance
 * @param {string} characterName - The character name for the pill label
 * @param {boolean} deferStart - Task #12 Safari A2F 0-from-0: unlock the
 *   element (play → pause → rewind to 0) instead of letting audio run
 *   ahead of the blendshape stream
 */
async function tryPlayGreetingAudio(
    audioEl,
    text,
    chatController,
    characterName = null,
    deferStart = false,
) {
    if (window.volumeController) {
        try {
            audioEl.volume = window.volumeController.getCurrentVolume();
        } catch {}
    }

    const showTextFallback = () => {
        if (chatController) {
            if (chatController.chatContainer) {
                chatController.chatContainer.classList.add("visible");
            }
            chatController.addMessageToChat("", text, "bot-message");
            chatController.addToConversationHistory?.("assistant", text);
        }
    };

    const startPlayback = async () => {
        await audioEl.play();
        if (deferStart) {
            // Task #12: unlock-then-hold. play() has blessed this element
            // for unmuted programmatic playback, so pause immediately and
            // rewind to 0. The REAL start (from 0) happens later, aligned
            // to the first buffered A2F frames — the exact 0-from-0
            // lip-sync of every other browser instead of letting audio
            // race ahead of the blendshape stream.
            audioEl.pause();
            try { audioEl.currentTime = 0; } catch (_) {}
            console.log(
                "[Greeting] Audio element unlocked, held at 0 for A2F sync",
            );
        }
    };

    try {
        await startPlayback();
        // Voice is committing — record the hint flag and tear down the
        // page-wide gesture harvest (and pill, if any) so mid-speech clicks
        // can never re-bless the element or cut audio.
        setGreetingAudioGranted();
        commitGreetingAudioUnlock();
        return true;
    } catch (firstErr) {
        console.warn(
            "[Greeting] play() blocked/failed on first attempt:",
            firstErr?.name || firstErr,
        );
    }

    // Blocked. With ZERO interaction so far this is autoplay policy, not a
    // real failure → hold the spoken greeting for the first tap anywhere
    // (the pill is a beat in the flow, not a permission card). If a gesture
    // ALREADY unlocked audio and play() still failed, it is a genuine
    // playback failure → honest text fallback below.
    if (!hasUnlockGesture()) {
        const started = await new Promise((resolve) => {
            const armed = armGreetingStart(
                () => {
                    // Runs synchronously inside the gesture handler so
                    // play() carries the user activation.
                    startPlayback().then(
                        () => resolve(true),
                        (err) => {
                            console.error(
                                "[Greeting] play() failed inside the unlock gesture:",
                                err,
                            );
                            resolve(false);
                        },
                    );
                },
                { showPill: true, characterName },
            );
            if (!armed) resolve(false); // kit already committed — no hold possible
        });
        if (started) {
            setGreetingAudioGranted();
            commitGreetingAudioUnlock();
            return true;
        }
    }

    // Genuine failure → text greeting; the caller restores mic/stop UI.
    commitGreetingAudioUnlock();
    showTextFallback();
    return false;
}

// ==============================
// GreetingService
// ==============================
/**
 * Manages character greeting playback with lipsync support
 */
export class GreetingService {
    constructor() {
        this.currentAudio = null;
        this.isPlaying = false;
        this.greetingCanceled = false;
        this.greetingPlayed = false;
        this.lipSyncController = null;
        this.animationController = null;
        this.modelViewer = null;
        this.chatController = null;

        this.a2fDriver = null;
        this._a2fWs = null;
        this._a2fGen = 0;
        this._a2fSessionActive = false;

        // Greeting-path sync gate: holds audio.play() until ≥120ms of A2F
        // blendshape frames are buffered, mirroring the chat-path sync gate.
        this._a2fGreetingSyncGate = null;
        this._a2fGreetingSyncGateResolve = null;
        this._a2fGreetingGateTimer = null;

        // Task #12: greeting teardown safety net. `_greetingTeardownRan`
        // is a per-greeting one-shot so the natural "ended" event and the
        // watchdog can race without ever running the teardown twice;
        // `_greetingTeardownWatchdog` is the polling interval handle.
        this._greetingTeardownRan = false;
        this._greetingTeardownWatchdog = null;

        // Forward-pacer state: a timer handle for the wall-clock paced
        // release of decoded PCM chunks to the A2F bridge. Cleared on
        // session stop. The speaker <audio> element is never throttled.
        this._greetingPacerTimer = null;

        // Task #99: the speaker <audio> element is NEVER paused. The
        // greeting plays a single HTMLAudioElement to completion; the
        // mouth closes on its natural `ended` event with a duration /
        // progress watchdog as backstop. No pause-on-underrun gate.
        // Kept: set true once the bridge sends a2f_done.
        this._a2fGreetingDoneReceived = false;

        // Task #519: per-greeting generation stamp + the current greeting's
        // listener-detach hook (see playPersonalizedGreeting). Needed
        // because Safari greetings reuse the kit's ONE persistent blessed
        // element across greetings, so stale listeners/timers must be able
        // to recognise they've been superseded.
        this._greetingGen = 0;
        this._detachGreetingAudioListeners = null;
    }

    // ================
    // Wiring setters
    // ================
    setLipSyncController(controller) {
        this.lipSyncController = controller;
    }
    setAnimationController(controller) {
        this.animationController = controller;
    }
    setModelViewer(modelViewer) {
        this.modelViewer = modelViewer;
    }
    setChatController(chatController) {
        this.chatController = chatController;
    }
    setA2FLipSyncDriver(driver) {
        this.a2fDriver = driver;
    }

    // ================
    // A2F Helpers
    // ================

    _getOrCreateA2FDriver() {
        if (this.a2fDriver) return this.a2fDriver;
        if (!this.lipSyncController?.blendShapeControls) return null;
        const modelId = this.modelViewer?.currentModelKey || null;
        if (!shouldUseA2FLipSync(modelId)) return null;
        this.a2fDriver = new A2FLipSyncDriver({
            blendShapeControls: this.lipSyncController.blendShapeControls,
            debug: window.__A2F_DEBUG__ === true,
        });
        console.log("[GreetingA2F] Created A2F driver for model:", modelId);
        return this.a2fDriver;
    }

    /**
     * Arm (or re-arm) the greeting-path sync gate.
     * Must be called synchronously before any async work that might let
     * canplaythrough fire, otherwise the handler may see a null gate.
     * A fallback timeout guarantees audio still starts if frames never
     * arrive (network failure, A2F unavailable, etc.). Task #99: raised to
     * 5s so it sits above the ~1.8s frame-lead gate and never releases
     * playback early on a healthy (just slightly slow) bridge.
     */
    _armGreetingSyncGate() {
        // Clear any previous gate/timer first.
        if (this._a2fGreetingGateTimer !== null) {
            clearTimeout(this._a2fGreetingGateTimer);
            this._a2fGreetingGateTimer = null;
        }
        if (this._a2fGreetingSyncGateResolve) {
            this._a2fGreetingSyncGateResolve();
            this._a2fGreetingSyncGateResolve = null;
        }

        let resolve;
        this._a2fGreetingSyncGate = new Promise((res) => { resolve = res; });
        this._a2fGreetingSyncGateResolve = resolve;

        this._a2fGreetingGateTimer = setTimeout(() => {
            this._a2fGreetingGateTimer = null;
            if (this._a2fGreetingSyncGateResolve === resolve) {
                console.warn("[GreetingA2F] Sync gate timeout — releasing gate without minimum frames");
                this._a2fGreetingSyncGateResolve();
                this._a2fGreetingSyncGateResolve = null;
            }
        }, 5000);
    }

    _stopA2FSession() {
        this._a2fSessionActive = false;
        // Stop the forward-pacer so no stale chunks are sent after stop.
        if (this._greetingPacerTimer !== null) {
            clearTimeout(this._greetingPacerTimer);
            this._greetingPacerTimer = null;
        }
        if (this._a2fWs) {
            try { this._a2fWs.close(); } catch (_) {}
            this._a2fWs = null;
        }
        if (this.a2fDriver) {
            try { this.a2fDriver.markSessionEnded(); } catch (_) {}
            try { this.a2fDriver.reset(); } catch (_) {}
        }
        // Release the sync gate so canplaythrough is never left waiting.
        if (this._a2fGreetingGateTimer !== null) {
            clearTimeout(this._a2fGreetingGateTimer);
            this._a2fGreetingGateTimer = null;
        }
        if (this._a2fGreetingSyncGateResolve) {
            this._a2fGreetingSyncGateResolve();
            this._a2fGreetingSyncGateResolve = null;
        }
        this._a2fGreetingSyncGate = null;
    }

    async _decodeAudioBlobToPCM(audioBlob) {
        const arrayBuffer = await audioBlob.arrayBuffer();
        const audioCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
        let decoded;
        try {
            decoded = await audioCtx.decodeAudioData(arrayBuffer);
        } finally {
            try { audioCtx.close(); } catch (_) {}
        }
        const channelData = decoded.getChannelData(0);
        const int16 = new Int16Array(channelData.length);
        for (let i = 0; i < channelData.length; i++) {
            const s = Math.max(-1, Math.min(1, channelData[i]));
            int16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
        }
        return new Uint8Array(int16.buffer);
    }

    _streamGreetingThroughA2F(audioBlob) {
        const driver = this._getOrCreateA2FDriver();
        if (!driver) return Promise.reject(new Error("No A2F driver"));
        this._stopA2FSession();

        const gen = ++this._a2fGen;
        this._a2fSessionActive = true;
        this._a2fGreetingDoneReceived = false;

        return new Promise((resolve, reject) => {
            const proto = location.protocol === "https:" ? "wss:" : "ws:";
            const _buildWs = async () => {
                let url = `${proto}//${location.host}/a2f-bridge`;
                try {
                    const user = window.firebaseAuth?.currentUser;
                    if (user) {
                        const token = await user.getIdToken();
                        if (token) url += `?token=${encodeURIComponent(token)}`;
                    }
                } catch (_) {}
                return new WebSocket(url);
            };
            _buildWs().then((ws) => {
            this._a2fWs = ws;

            const cleanup = () => {
                this._a2fSessionActive = false;
                if (this._a2fWs === ws) this._a2fWs = null;
                try { ws.close(); } catch (_) {}
            };

            ws.addEventListener("open", () => {
                ws.send(JSON.stringify({
                    type: "start",
                    sampleRate: 16000,
                    channels: 1,
                    bitsPerSample: 16,
                    gen,
                }));
            });

            ws.addEventListener("message", (ev) => {
                let msg;
                try { msg = JSON.parse(ev.data); } catch (_) { return; }

                if (msg.type === "started") {
                    this._decodeAudioBlobToPCM(audioBlob).then((pcmBytes) => {
                        if (!this._a2fSessionActive || ws.readyState !== WebSocket.OPEN) return;
                        // Forward-pace the bridge-bound PCM to ~real-time
                        // after a short warm-up instead of dumping the whole
                        // clip in one tight loop. This stops the greeting
                        // from flooding the bridge (which starves lip-sync on
                        // long greetings). The `end` signal is deferred until
                        // the paced queue drains. Mirrors the chat path.
                        this._startGreetingPacer(pcmBytes, ws, gen);
                    }).catch((err) => {
                        console.error("[GreetingA2F] PCM decode error:", err);
                        cleanup();
                        reject(err);
                    });

                } else if (msg.type === "a2f_header") {
                    console.log("[GreetingA2F] Header received:", msg.blendshape_names?.length, "blendshapes");

                } else if (msg.type === "a2f_frames") {
                    if (msg.gen !== undefined && msg.gen !== gen) return;
                    if (!this._a2fSessionActive) return;
                    const frames = msg.frames || [];
                    if (frames.length > 0) {
                        driver.enqueueFrames(frames, { autoStart: false });
                        // Task #99 (math fix): hold playback until ≥1800ms of
                        // blendshape frames are buffered — same lead the chat
                        // path now uses. The A2F pipeline takes ~1.3s to turn
                        // audio into frames, so the old 120ms gate let the
                        // mouth start almost immediately and then run dry
                        // mid-clip (the "skips middle words" symptom). With the
                        // pacer feeding the bridge at ~1× real-time, a constant
                        // ~1.8s lead keeps frames ahead of playback for the
                        // whole greeting without ever pausing the speaker.
                        if (this._a2fGreetingSyncGateResolve) {
                            const lastT = frames[frames.length - 1].time_ms ?? 0;
                            if (lastT >= 1800) {
                                console.log("[GreetingA2F] Sync gate released — buffered to", lastT, "ms");
                                if (this._a2fGreetingGateTimer !== null) {
                                    clearTimeout(this._a2fGreetingGateTimer);
                                    this._a2fGreetingGateTimer = null;
                                }
                                this._a2fGreetingSyncGateResolve();
                                this._a2fGreetingSyncGateResolve = null;
                            }
                        }
                    }

                } else if (msg.type === "a2f_done") {
                    // No more frames are coming — let the underrun gate
                    // resume the tail instead of waiting for a refill that
                    // will never arrive.
                    this._a2fGreetingDoneReceived = true;
                    driver.markSessionEnded();
                    cleanup();
                    resolve();

                } else if (msg.type === "error") {
                    if (msg.code === "unauthenticated") {
                        console.warn(
                            "[GreetingA2F][DEGRADED] Bridge rejected connection: unauthenticated — " +
                            "A2F lip-sync unavailable, falling back to phoneme lip-sync (reduced mouth quality).",
                        );
                    } else {
                        console.warn(
                            "[GreetingA2F][DEGRADED] Bridge error — A2F lip-sync unavailable, " +
                            "falling back to phoneme lip-sync (reduced mouth quality). Reason:",
                            msg.message,
                        );
                    }
                    cleanup();
                    reject(new Error(msg.message));
                }
            });

            ws.addEventListener("error", (err) => {
                console.error("[GreetingA2F] WebSocket error:", err);
                cleanup();
                reject(new Error("A2F WebSocket error"));
            });

            ws.addEventListener("close", () => {
                if (this._a2fSessionActive) {
                    this._a2fSessionActive = false;
                    if (this._a2fWs === ws) this._a2fWs = null;
                }
            });
            }).catch((err) => {
                console.error("[GreetingA2F] Failed to build WebSocket:", err);
                reject(err);
            });
        });
    }

    // ================
    // A2F forward-pacer (bridge-bound copy only)
    // ================
    /**
     * Release the decoded greeting PCM to the A2F bridge on a wall-clock
     * schedule so cumulative audio-ms forwarded never runs faster than
     * RATE × wall-time, after a short unthrottled warm-up. The `end`
     * signal is deferred until the queue drains. The speaker <audio>
     * element is never touched here.
     *
     * Mirrors `RealtimeLLMToElevenLabs._a2fPacerKick`.
     */
    _startGreetingPacer(pcmBytes, ws, gen) {
        if (this._greetingPacerTimer !== null) {
            clearTimeout(this._greetingPacerTimer);
            this._greetingPacerTimer = null;
        }

        const total = pcmBytes.length;
        let offset = 0;
        let forwardedMs = 0;
        let engaged = false;
        let nextAllowedWall = 0;

        const sendChunkBytes = (bytes) => {
            let bin = "";
            for (let j = 0; j < bytes.length; j++) {
                bin += String.fromCharCode(bytes[j]);
            }
            ws.send(JSON.stringify({ type: "audio_base64", data: btoa(bin), gen }));
        };

        const kick = () => {
            this._greetingPacerTimer = null;
            while (offset < total) {
                if (
                    !this._a2fSessionActive ||
                    this._a2fGen !== gen ||
                    ws.readyState !== WebSocket.OPEN
                ) {
                    return; // session ended / superseded — abort silently
                }
                const now = performance.now();
                const bytes = Math.min(GREETING_A2F_PCM_CHUNK_BYTES, total - offset);
                const chunkMs = bytes / GREETING_A2F_PCM_BYTES_PER_MS;

                if (!GREETING_A2F_FORWARD_PACING_ENABLED || GREETING_A2F_FORWARD_PACING_RATE <= 0) {
                    sendChunkBytes(pcmBytes.subarray(offset, offset + bytes));
                    offset += bytes;
                    continue;
                }

                // Warm-up window: send unthrottled until cumulative
                // forwarded audio-ms exceeds WARMUP_MS so the bridge can
                // produce the first lip-sync frame quickly.
                if (!engaged) {
                    if (forwardedMs + chunkMs <= GREETING_A2F_FORWARD_PACING_WARMUP_MS) {
                        sendChunkBytes(pcmBytes.subarray(offset, offset + bytes));
                        offset += bytes;
                        forwardedMs += chunkMs;
                        continue;
                    }
                    engaged = true;
                    nextAllowedWall = now;
                }

                if (now >= nextAllowedWall) {
                    sendChunkBytes(pcmBytes.subarray(offset, offset + bytes));
                    offset += bytes;
                    forwardedMs += chunkMs;
                    nextAllowedWall = now + chunkMs / GREETING_A2F_FORWARD_PACING_RATE;
                    continue;
                }

                const wait = Math.max(1, nextAllowedWall - now);
                this._greetingPacerTimer = setTimeout(kick, wait);
                return;
            }

            // Queue drained — emit the deferred end-of-audio signal.
            if (
                this._a2fSessionActive &&
                this._a2fGen === gen &&
                ws.readyState === WebSocket.OPEN
            ) {
                ws.send(JSON.stringify({ type: "end", gen }));
            }
        };

        kick();
    }

    // ================
    // Internals
    // ================
    _showTextGreetingOnly(text) {
        if (this.chatController) {
            // Ensure the chat container is visible first
            if (this.chatController.chatContainer) {
                this.chatController.chatContainer.classList.add("visible");
            }
            // Add the message to chat
            this.chatController.addMessageToChat("", text, "bot-message");
            this.chatController.addToConversationHistory?.("assistant", text);
        }

        // Force idle animation restart for Safari text-only greetings
        // Use a small delay to ensure any stopping operations have completed
        if (this.animationController && isSafari()) {
            // For Safari, force the idle animation to restart
            // by first transitioning to a different state then back to idle
            setTimeout(() => {
                // Force a fresh idle animation start
                if (this.animationController.currentState === "IDLE") {
                    // Briefly transition away then back to force restart
                    this.animationController.currentState = "TRANSITIONING";
                }
                this.animationController.playIdleAnimation();
            }, 100);
        }
    }

    async prepareEnhancedLipSync(text) {
        try {
            const response = await fetch("/api/greeting-timing", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ text }),
            });
            if (!response.ok) {
                this.lipSyncController?.preparePhonemes?.(text);
                return;
            }
            const data = await response.json();
            if (data?.success && data?.timing_data) {
                this.lipSyncController &&
                    (this.lipSyncController.preparedPhonemes = {
                        useElevenLabsTiming: true,
                        phonemeSequence: data.timing_data.phonemeSequence || [],
                        durationSequence:
                            data.timing_data.durationSequence || [],
                    });
            } else {
                this.lipSyncController?.preparePhonemes?.(text);
            }
        } catch {
            this.lipSyncController?.preparePhonemes?.(text);
        }
    }

    async getUserInfo() {
        try {
            if (window.firebaseAuth && window.firebaseModules) {
                const user = window.firebaseAuth.currentUser;
                if (user) {
                    const userProfileRef = window.firebaseModules.doc(
                        window.firebaseDB,
                        "userProfiles",
                        user.uid,
                    );
                    const userProfileDoc =
                        await window.firebaseModules.getDoc(userProfileRef);
                    if (userProfileDoc.exists()) {
                        const p = userProfileDoc.data();
                        return {
                            isAuthenticated: true,
                            username:
                                p.username ||
                                user.displayName ||
                                user.email.split("@")[0],
                            uid: user.uid,
                        };
                    }
                    return {
                        isAuthenticated: true,
                        username: user.displayName || user.email.split("@")[0],
                        uid: user.uid,
                    };
                }
            }
            return { isAuthenticated: false, username: null, uid: null };
        } catch {
            return { isAuthenticated: false, username: null, uid: null };
        }
    }

    async generatePersonalizedGreeting(characterData, username) {
        const modelId = this.modelViewer?.currentModelKey || null;
        // Safari-safe: send ONLY the IANA timezone. The server uses its own
        // UTC clock to compute the actual hour in that zone — never the
        // browser's device clock. See ClientTimeOfDay.js for rationale.
        const clientTime = getClientTimeInfo();
        const clientTimezone = clientTime ? clientTime.timeZone : null;
        const resp = await fetch("/api/generate-personalized-greeting", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                character_name: characterData.name,
                username,
                character_id: modelId,
                client_timezone: clientTimezone,
            }),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!data.success)
            throw new Error(
                data.error || "Failed to generate personalized greeting",
            );
        return data; // { greeting_text, audio_base64, timing_data }
    }

    async generateConversationMemoryGreeting(
        characterData,
        username,
        lastConversation,
    ) {
        const modelId =
            this.modelViewer?.currentModelKey ||
            characterData.id ||
            characterData.characterId ||
            characterData.uid ||
            "";
        const clientTime = getClientTimeInfo();
        const clientTimezone = clientTime ? clientTime.timeZone : null;
        const payload = {
            characterName: characterData.name,
            characterData: { ...characterData, id: modelId },
            username,
            lastConversation,
            client_timezone: clientTimezone,
        };
        const resp = await fetch("/api/generate-conversation-memory-greeting", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!data.success)
            throw new Error(
                data.error || "Failed to generate conversation memory greeting",
            );
        return data; // { greeting, audio_base64, timing_data }
    }

    // ==============================
    // Personalized / Memory Playback
    // ==============================
    async playPersonalizedGreeting(personalizedData, characterData) {
        if (this.greetingCanceled) return;

        // Task #12: fresh greeting → reset the teardown one-shot and clear
        // any stale watchdog left over from a previous greeting.
        this._greetingTeardownRan = false;
        if (this._greetingTeardownWatchdog !== null) {
            clearInterval(this._greetingTeardownWatchdog);
            this._greetingTeardownWatchdog = null;
        }

        // Task #519: per-greeting generation + stale-listener detach. On
        // Safari greetings play on the kit's ONE persistent blessed element
        // (see below), so listeners left behind by an earlier or stopped
        // greeting MUST be removed — a stale "ended" firing at the end of
        // the NEXT greeting would tear down its live A2F session. The
        // generation stamp makes every per-greeting handler self-ignoring
        // once a newer greeting owns the element.
        const myGen = ++this._greetingGen;
        try {
            this._detachGreetingAudioListeners?.();
        } catch (_) {}

        const greetingText = personalizedData.greeting_text;
        const audioBase64 = personalizedData.audio_base64;
        const timingData = personalizedData.timing_data;

        // Always show the message in chat first (so the user sees something even if audio fails)
        this._showTextGreetingOnly(greetingText);

        const modelId = this.modelViewer?.currentModelKey || null;
        const useA2F = !!(shouldUseA2FLipSync(modelId) && this._getOrCreateA2FDriver());

        return new Promise((resolve, reject) => {
            // Setup audio element from base64.
            // Task #519: Safari blesses audio PER-ELEMENT, so the greeting
            // plays on the kit's persistent element — the one every early
            // gesture already blessed with the real silent clip (mirrors
            // the /learn narration-element contract). Claiming it stops
            // further blessing while the greeting owns it. Other browsers
            // keep a fresh element per greeting, exactly as before.
            const audio = isSafari()
                ? claimGreetingAudioElement()
                : new Audio();
            this.currentAudio = audio;

            const audioBlob = new Blob(
                [
                    Uint8Array.from(atob(audioBase64 || ""), (c) =>
                        c.charCodeAt(0),
                    ),
                ],
                { type: "audio/mpeg" },
            );
            const audioUrl = URL.createObjectURL(audioBlob);

            // UI: show stop button / hide mic during greeting attempt
            if (this.chatController?.stopAudioButton) {
                this.chatController.stopAudioButton.classList.remove("d-none");
            }
            if (this.chatController?.micButton) {
                this.chatController.micButton.classList.add("d-none");
            }

            audio.src = audioUrl;
            audio.preload = "auto";

            let lipSyncStarted = false;
            let lipSyncCanceled = false;
            let a2fFailed = false;
            // Task #519: true while a play attempt (including the
            // hold-until-first-tap arm) is in flight — the 3 s safety retry
            // must not double-arm or double-play.
            let playAttemptInFlight = false;

            // Task #12: single teardown path (one-shot) shared by the
            // natural "ended" event and the safety-net watchdog below.
            // Safari sometimes never fires "ended" (the AudioContext just
            // resumed from the popup gesture can misbehave) and the
            // bridge's a2f_done can be missed — either case leaves the
            // mouth frozen open and the speak animation looping until the
            // user clicks Stop. Routing both through one guarded helper
            // guarantees the mouth closes and idle resumes exactly once.
            const finishGreeting = (reason) => {
                if (this._greetingTeardownRan) return;
                this._greetingTeardownRan = true;
                if (this._greetingTeardownWatchdog !== null) {
                    clearInterval(this._greetingTeardownWatchdog);
                    this._greetingTeardownWatchdog = null;
                }
                if (reason && reason.indexOf("watchdog") === 0) {
                    console.warn(
                        "[GreetingA2F] Safety-net watchdog fired greeting teardown — " +
                        "audio 'ended' was wedged (reason=" + reason + ")",
                    );
                } else {
                    console.log("[GreetingA2F] Greeting teardown ran reason=" + reason);
                }

                this.isPlaying = false;
                this.currentAudio = null;

                if (this.chatController?.stopAudioButton)
                    this.chatController.stopAudioButton.classList.add("d-none");
                if (this.chatController?.sendButton)
                    this.chatController.sendButton.classList.remove("d-none");

                if (this.chatController?.micButtonContainer) {
                    this.chatController.micButtonContainer.classList.remove(
                        "d-none",
                    );
                    this.chatController.micButtonContainer.style.display = "";
                }
                if (this.chatController?.micButton) {
                    this.chatController.micButton.classList.remove(
                        "d-none",
                        "disabled",
                    );
                    this.chatController.micButton.style.display = "";
                    this.chatController.micButton.disabled = false;
                    this.chatController.checkMicrophoneAvailability?.();
                }

                if (useA2F && !a2fFailed) {
                    this._stopA2FSession();
                } else {
                    this.lipSyncController?.stopAnimation?.();
                }
                this.animationController?.onTTSEnd?.();

                // Task #519: remove this greeting's element listeners (the
                // Safari persistent element outlives the greeting) and
                // release the claim so early-gesture blessing may resume.
                try {
                    this._detachGreetingAudioListeners?.();
                } catch (_) {}

                try { URL.revokeObjectURL(audioUrl); } catch (_) {}
                resolve();
            };

            if (useA2F) {
                // Arm the gate synchronously before any async work so that
                // canplaythrough (which may fire immediately for blob URLs)
                // always sees a valid gate to await.
                this._armGreetingSyncGate();
                console.log("[GreetingA2F] Starting A2F stream for personalized greeting (model:", modelId, ")");
                this._streamGreetingThroughA2F(audioBlob).catch((err) => {
                    console.warn("[GreetingA2F] A2F stream error, falling back to phoneme:", err);
                    a2fFailed = true;
                    if (lipSyncStarted && this.lipSyncController && timingData) {
                        this.lipSyncController.preparedPhonemes = {
                            useElevenLabsTiming: true,
                            phonemeSequence: timingData.phonemeSequence || [],
                            durationSequence: timingData.durationSequence || [],
                        };
                        this.lipSyncController.startPreparedAnimation?.();
                    }
                });
            }

            audio.addEventListener(
                "canplaythrough",
                async () => {
                    // Task #519: stale-once listener from a previous greeting
                    // on the reused Safari element — a newer greeting owns it.
                    if (this._greetingGen !== myGen) return;
                    if (
                        lipSyncStarted ||
                        lipSyncCanceled ||
                        this.greetingCanceled
                    )
                        return;

                    if (this.animationController)
                        this.animationController.onTTSStart();

                    if ((!useA2F || a2fFailed) && this.lipSyncController && timingData) {
                        this.lipSyncController.preparedPhonemes = {
                            useElevenLabsTiming: true,
                            phonemeSequence: timingData.phonemeSequence || [],
                            durationSequence: timingData.durationSequence || [],
                        };
                    }

                    if (this.greetingCanceled) {
                        URL.revokeObjectURL(audioUrl);
                        return reject(
                            new Error("Personalized greeting canceled"),
                        );
                    }

                    // Wait for A2F blendshape frames to buffer before starting
                    // audio — prevents the "audio before animation" gap caused by
                    // gRPC session setup latency.  Skipped on Safari (autoplay
                    // policy requires play() to be synchronous after user gesture).
                    if (useA2F && !a2fFailed && this._a2fGreetingSyncGate && !isSafari()) {
                        await this._a2fGreetingSyncGate;
                        if (this.greetingCanceled) {
                            URL.revokeObjectURL(audioUrl);
                            return reject(new Error("Personalized greeting canceled"));
                        }
                    }

                    // Capture timestamp before play() for accurate clock sync.
                    const playStartTime = performance.now();

                    // Task #12 → #519: on Safari, A2F lip-sync needs the SAME
                    // 0-from-0 alignment as every other browser. Whatever
                    // gesture (or allowed autoplay) first lets play() succeed
                    // is used to UNLOCK the audio element (play → immediately
                    // pause → rewind to 0) WITHOUT letting it race ahead of
                    // the blendshape stream. The real playback start happens
                    // below, once A2F frames have buffered.
                    const safariDeferA2F = isSafari() && useA2F && !a2fFailed;

                    // Try to start audio: attempt play() outright; if blocked
                    // with zero interaction, HOLD for the first tap (pill).
                    // If not started → text-only path (genuine failure).
                    playAttemptInFlight = true;
                    let started = false;
                    try {
                        started = await tryPlayGreetingAudio(
                            audio,
                            greetingText,
                            this.chatController,
                            characterData?.name,
                            safariDeferA2F,
                        );
                    } finally {
                        playAttemptInFlight = false;
                    }

                    if (started && this.greetingCanceled) {
                        // Task #519: stopped while holding for the first tap —
                        // don't start drivers against a dead greeting. (The
                        // stop click itself is a gesture, so the armed start
                        // may fire just before stop() lands.)
                        try {
                            this._detachGreetingAudioListeners?.();
                        } catch (_) {}
                        try {
                            audio.pause();
                        } catch (_) {}
                        URL.revokeObjectURL(audioUrl);
                        return resolve();
                    }

                    if (!started) {
                        // Cleanup UI for text-only mode
                        this.isPlaying = false;
                        this.currentAudio = null;
                        if (useA2F && !a2fFailed) {
                            this._stopA2FSession();
                        } else {
                            this.lipSyncController?.stopAnimation?.();
                        }
                        if (this.chatController?.stopAudioButton)
                            this.chatController.stopAudioButton.classList.add(
                                "d-none",
                            );
                        if (this.chatController?.micButtonContainer) {
                            this.chatController.micButtonContainer.classList.remove(
                                "d-none",
                            );
                            this.chatController.micButtonContainer.style.display =
                                "";
                        }
                        if (this.chatController?.micButton) {
                            this.chatController.micButton.classList.remove(
                                "d-none",
                                "disabled",
                            );
                            this.chatController.micButton.style.display = "";
                            this.chatController.micButton.disabled = false;
                            this.chatController.checkMicrophoneAvailability?.();
                        }
                        // Task #519: drop this greeting's listeners + claim.
                        try {
                            this._detachGreetingAudioListeners?.();
                        } catch (_) {}
                        URL.revokeObjectURL(audioUrl);
                        return resolve(); // text-only already shown
                    }

                    // Audio path
                    this.isPlaying = true;

                    if (useA2F && !a2fFailed && this.a2fDriver) {
                        if (safariDeferA2F && this._a2fGreetingSyncGate) {
                            // Safari: the element is unlocked but paused at 0.
                            // Wait for A2F frames to buffer (the gate has its own
                            // 3 s ceiling), THEN start audio and the driver
                            // together from 0 — identical alignment to every
                            // other browser. No clock offset, no skipped frames.
                            await this._a2fGreetingSyncGate;
                            if (
                                this.greetingCanceled ||
                                a2fFailed ||
                                !this.a2fDriver
                            ) {
                                try { URL.revokeObjectURL(audioUrl); } catch (_) {}
                                return;
                            }
                            if (this.animationController)
                                this.animationController.onAudioPlaybackStart();
                            const safariPlayStart = performance.now();
                            let safariPlayOk = true;
                            try {
                                await audio.play();
                            } catch (e) {
                                // CRITICAL: if the deferred play() is rejected the
                                // element never advances, so the driver must NOT
                                // start — otherwise the clock runs against silent
                                // audio and the underrun gate would pause forever.
                                safariPlayOk = false;
                                console.warn(
                                    "[GreetingA2F] Safari deferred play() failed — driver not started:",
                                    e,
                                );
                            }
                            if (safariPlayOk) {
                                this.a2fDriver.syncClock(0, safariPlayStart);
                                this.a2fDriver.startPlaybackNow();
                                console.log(
                                    "[GreetingA2F] A2F driver playback started (Safari, 0-from-0 after frame buffer)",
                                );
                            }
                        } else {
                            // Non-Safari: the gate was already awaited before
                            // play(), so audio just started — anchor the A2F
                            // clock origin to the pre-play timestamp.
                            if (this.animationController)
                                this.animationController.onAudioPlaybackStart();
                            this.a2fDriver.syncClock(0, playStartTime);
                            this.a2fDriver.startPlaybackNow();
                            console.log("[GreetingA2F] A2F driver playback started");
                        }
                    } else {
                        if (this.animationController)
                            this.animationController.onAudioPlaybackStart();
                        if (
                            (!useA2F || a2fFailed) &&
                            this.lipSyncController &&
                            !lipSyncStarted
                        ) {
                            lipSyncStarted = true;
                            this.lipSyncController.startPreparedAnimation();
                        }
                    }

                    // Task #12: greeting teardown safety-net watchdog. Polls
                    // real audio progress; if playback finishes but the
                    // "ended" event never fires (a known Safari issue when the
                    // AudioContext was just resumed from the popup gesture) the
                    // mouth would otherwise stay frozen open and the speak
                    // animation would loop until a manual Stop. Forcing the
                    // shared teardown here guarantees the mouth closes and idle
                    // resumes on its own.
                    if (this._greetingTeardownWatchdog !== null) {
                        clearInterval(this._greetingTeardownWatchdog);
                    }
                    let _wdLastCt = 0;
                    let _wdLastAdvanceWall = performance.now();
                    this._greetingTeardownWatchdog = setInterval(() => {
                        if (this._greetingTeardownRan) return;
                        const a = this.currentAudio;
                        if (!a) return;
                        const now = performance.now();
                        const ct = a.currentTime || 0;
                        const dur =
                            isFinite(a.duration) && a.duration > 0
                                ? a.duration
                                : null;
                        if (ct > _wdLastCt + 0.01) {
                            _wdLastCt = ct;
                            _wdLastAdvanceWall = now;
                        }
                        const stalledFor = now - _wdLastAdvanceWall;
                        if (a.ended) {
                            finishGreeting("watchdog_ended");
                        } else if (
                            dur != null &&
                            ct >= dur - 0.25 &&
                            stalledFor >= 800
                        ) {
                            // At (or within 250 ms of) the end and the clock
                            // hasn't advanced for 800 ms → playback finished
                            // but "ended" never fired.
                            finishGreeting("watchdog_near_end_stall");
                        } else if (a.paused && ct > 0.05 && stalledFor >= 1500) {
                            // Duration unknown (Safari MP3 blobs sometimes
                            // report Infinity/NaN): playback is paused at a
                            // non-zero position with no progress for 1.5 s →
                            // treat as finished.
                            finishGreeting("watchdog_paused_stall");
                        }
                    }, 250);
                },
                { once: true },
            );

            // Task #519: named handlers + a detach helper registered on the
            // instance, so finishGreeting/stop() can remove them. On Safari
            // the element is the kit's persistent one and OUTLIVES this
            // greeting — stale listeners would otherwise fire during the
            // NEXT greeting and tear down its live A2F session. The gen
            // guards are belt-and-braces for the same reason.
            const onGreetingEnded = () => {
                if (this._greetingGen !== myGen) return;
                finishGreeting("ended");
            };

            const onGreetingError = (e) => {
                if (this._greetingGen !== myGen) return;
                if (this._greetingTeardownRan) return;
                this._greetingTeardownRan = true;
                if (this._greetingTeardownWatchdog !== null) {
                    clearInterval(this._greetingTeardownWatchdog);
                    this._greetingTeardownWatchdog = null;
                }
                this.isPlaying = false;
                this.currentAudio = null;
                lipSyncCanceled = true;

                // Keep the text we already showed; just restore controls
                if (this.chatController?.stopAudioButton)
                    this.chatController.stopAudioButton.classList.add("d-none");
                if (this.chatController?.sendButton)
                    this.chatController.sendButton.classList.remove("d-none");

                if (this.chatController?.micButtonContainer) {
                    this.chatController.micButtonContainer.classList.remove(
                        "d-none",
                    );
                    this.chatController.micButtonContainer.style.display = "";
                }
                if (this.chatController?.micButton) {
                    this.chatController.micButton.classList.remove(
                        "d-none",
                        "disabled",
                    );
                    this.chatController.micButton.style.display = "";
                    this.chatController.micButton.disabled = false;
                    this.chatController.checkMicrophoneAvailability?.();
                }

                if (useA2F) {
                    this._stopA2FSession();
                } else {
                    this.lipSyncController?.stopAnimation?.();
                }
                detachGreetingAudioListeners();
                URL.revokeObjectURL(audioUrl);
                resolve(); // resolve (we already rendered text)
            };

            const detachGreetingAudioListeners = () => {
                try {
                    audio.removeEventListener("ended", onGreetingEnded);
                } catch (_) {}
                try {
                    audio.removeEventListener("error", onGreetingError);
                } catch (_) {}
                if (
                    this._detachGreetingAudioListeners ===
                    detachGreetingAudioListeners
                ) {
                    this._detachGreetingAudioListeners = null;
                }
                // Release the persistent element claim (no-op for fresh
                // elements) so early-gesture blessing may resume while the
                // unlock kit is still live.
                releaseGreetingAudioElement();
            };
            this._detachGreetingAudioListeners = detachGreetingAudioListeners;

            audio.addEventListener("ended", onGreetingEnded);
            audio.addEventListener("error", onGreetingError);

            // Safety timeout
            setTimeout(async () => {
                if (this.greetingCanceled || this.isPlaying) return;
                // Task #519: never double-arm/double-play — skip while the
                // main attempt (or its hold-until-first-tap) is in flight,
                // or once a newer greeting owns the element.
                if (playAttemptInFlight || this._greetingGen !== myGen) return;
                // Attempt playback once more; if blocked, we’re already in text mode
                try {
                    playAttemptInFlight = true;
                    let started = false;
                    try {
                        started = await tryPlayGreetingAudio(
                            audio,
                            greetingText,
                            this.chatController,
                            characterData?.name,
                        );
                    } finally {
                        playAttemptInFlight = false;
                    }
                    if (!started) {
                        try {
                            this._detachGreetingAudioListeners?.();
                        } catch (_) {}
                        URL.revokeObjectURL(audioUrl);
                        resolve();
                    }
                } catch {
                    URL.revokeObjectURL(audioUrl);
                    resolve();
                }
            }, 3000);
        });
    }

    // ==============================
    // Dynamic AI Greeting (Task #14)
    // Always calls the backend to generate a fresh in-character opener.
    // No hardcoded text, no pre-recorded mp3 fallback.
    // ==============================
    async playGreeting(characterData, tone = "happy") {
        try {
            // Only stop prior audio if not Safari (Safari doesn't play audio)
            if (!isSafari()) {
                this.stop(); // stop prior
            }
            this.greetingCanceled = false;
            this.greetingPlayed = true;

            // Show stop while greeting starts, hide send
            if (this.chatController?.stopAudioButton) {
                this.chatController.stopAudioButton.classList.remove("d-none");
            }
            if (this.chatController?.sendButton) {
                this.chatController.sendButton.classList.remove("d-none");
            }

            const userInfo = await this.getUserInfo();
            const username = userInfo?.username || null;

            if (this.greetingCanceled) return;

            // If the user is authenticated and we have a chat storage manager,
            // try to fetch their last conversation so the greeting can reference it.
            let lastConversation = null;
            try {
                if (userInfo?.isAuthenticated && window.chatStorageManager) {
                    lastConversation =
                        await window.chatStorageManager.getLastConversation(
                            characterData?.name,
                        );
                }
            } catch (e) {
                console.warn(
                    "[Greeting] Failed to fetch last conversation, continuing without memory:",
                    e,
                );
            }

            if (this.greetingCanceled) return;

            let greetingData = null;
            if (lastConversation) {
                const memoryData =
                    await this.generateConversationMemoryGreeting(
                        characterData,
                        username,
                        lastConversation,
                    );
                greetingData = {
                    greeting_text: memoryData.greeting,
                    audio_base64: memoryData.audio_base64,
                    timing_data: memoryData.timing_data,
                };
            } else {
                greetingData = await this.generatePersonalizedGreeting(
                    characterData,
                    username,
                );
            }

            if (this.greetingCanceled) return;

            // playPersonalizedGreeting will render the text and handle audio.
            return await this.playPersonalizedGreeting(
                greetingData,
                characterData,
            );
        } catch (err) {
            console.warn("[Greeting] Dynamic greeting failed:", err);
            // Minimal text-only fallback (no hardcoded character lines)
            const text = `Hello, I'm ${characterData?.name || "your guide"}.`;
            this._showTextGreetingOnly(text);

            // Restore controls so the user can keep using the chat
            if (this.chatController?.stopAudioButton)
                this.chatController.stopAudioButton.classList.add("d-none");
            if (this.chatController?.micButtonContainer) {
                this.chatController.micButtonContainer.classList.remove(
                    "d-none",
                );
                this.chatController.micButtonContainer.style.display = "";
            }
            if (this.chatController?.micButton) {
                this.chatController.micButton.classList.remove(
                    "d-none",
                    "disabled",
                );
                this.chatController.micButton.style.display = "";
                this.chatController.micButton.disabled = false;
                this.chatController.checkMicrophoneAvailability?.();
            }
        }
    }

    // ==============================
    // Stop
    // ==============================
    stop() {
        this.greetingCanceled = true;

        // Task #12: cancel the teardown watchdog and mark teardown done so
        // a late watchdog tick or "ended" event can't double-run after a
        // manual Stop. playPersonalizedGreeting resets the one-shot for the
        // next greeting.
        this._greetingTeardownRan = true;
        if (this._greetingTeardownWatchdog !== null) {
            clearInterval(this._greetingTeardownWatchdog);
            this._greetingTeardownWatchdog = null;
        }

        // Task #519: drop the current greeting's element listeners + claim.
        // With the persistent Safari element a stale "ended" from a stopped
        // greeting could otherwise fire during the NEXT greeting and kill
        // its live A2F session.
        try {
            this._detachGreetingAudioListeners?.();
        } catch (_) {}

        if (this.currentAudio && this.isPlaying) {
            this.currentAudio.pause();
            this.currentAudio.currentTime = 0;
            this.isPlaying = false;
            this.currentAudio = null;
        }
        if (this.currentAudio) {
            this.currentAudio.pause();
            this.currentAudio.currentTime = 0;
            this.currentAudio = null;
        }

        if (this.chatController?.stopAudioButton) {
            this.chatController.stopAudioButton.classList.add("d-none");
            if (this.chatController?.sendButton) {
                this.chatController.sendButton.classList.remove("d-none");
            }
        }

        // Stop any active A2F greeting session and release driver for recreation on next play
        this._stopA2FSession();
        this.a2fDriver = null;

        // Only stop lip sync animation if we're actually playing audio
        // On Safari with text-only greetings, we don't want to stop animations
        if (this.isPlaying || this.currentAudio) {
            this.lipSyncController?.stopAnimation?.();
        }
    }
}
